import { styled } from "styled-components";
import { Button, FormInstance } from "antd";
import { useAtomValue } from "jotai";
import dayjs from "dayjs";
import {
  MdAdd,
  MdClose,
  MdEdit,
  MdEmojiEvents,
  MdMilitaryTech,
} from "react-icons/md";
import { Award as AwardModel } from "../../../../models/film-profile.model";
import { SetState } from "../../../../types/SetState";
import { colors } from "../../../../theme/colors";
import { masterAtom } from "../../../../atoms/masters.atom";
import { valueToLabel } from "../../../../utils/functions/misc/valueToLabel";
import { countries } from "../../../../data/countries";
import { Section, card } from "../../components/section-card.component";

export const StepFive = ({
  awards,
  directors,
  producers,
  cast,
  crew,
  setIsShowingAwardModal,
  setAwards,
  setAwardEditingIndex,
  awardForm,
  visible = true,
}: {
  awards: AwardModel[];
  visible?: boolean;
  setAwards: SetState<AwardModel[]>;
  directors: {
    name: string;
    user?: string;
    biography: string;
    id?: string;
    type?: "professional" | "business";
  }[];
  producers: {
    name: string;
    user?: string;
    filmography: string;
    id?: string;
    type?: "professional" | "business";
  }[];
  cast: {
    name: string;
    user?: string;
    character: string;
    id?: string;
    type?: "professional" | "business";
  }[];
  crew: {
    name: string;
    user?: string;
    role: string;
    id?: string;
    type?: "professional" | "business";
  }[];
  setIsShowingAwardModal: SetState<boolean>;
  setAwardEditingIndex: SetState<number | null>;
  awardForm: FormInstance<AwardModel>;
}) => {
  const master = useAtomValue(masterAtom);

  const winnerFor = (award: AwardModel): string => {
    if (typeof award.award_winner === "number") {
      return (
        [
          ...directors.map((el) => el.name),
          ...producers.map((el) => el.name),
          ...cast.map((el) => el.name),
          ...crew.map((el) => el.name),
        ][award.award_winner] ?? ""
      );
    }
    return award.award_winner_name ?? "";
  };

  const onEdit = (index: number) => {
    const award = awards[index];
    setIsShowingAwardModal(true);
    setAwardEditingIndex(index);
    const adjusted = { ...award };
    adjusted.country =
      countries.find(
        (country) =>
          country.value.split("-")[1].toLowerCase() === award.country
      )?.value ?? award.country;
    awardForm.setFieldsValue(adjusted);
  };

  const onRemove = (index: number) => {
    setAwards(awards.filter((_, i) => i !== index));
  };

  return (
    <Wrapper style={{ display: visible ? "flex" : "none" }}>
      <Section
        icon={<MdEmojiEvents />}
        title="Awards & Festivals"
        subtitle="Add the festivals your film has played at and the awards it has earned. This step is optional."
      >
        <ListShell>
          {awards.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <MdMilitaryTech />
              </EmptyIcon>
              <EmptyTitle>No awards added yet</EmptyTitle>
              <EmptyHint>
                Showcase the festivals and honors your film has received.
              </EmptyHint>
            </EmptyState>
          ) : (
            <RowList>
              {awards.map((award, index) => (
                <AwardCard
                  key={award.id ?? `award-${index}`}
                  award={award}
                  winner={winnerFor(award)}
                  category={
                    (valueToLabel(
                      "award_category",
                      award.award_category,
                      master
                    ) as string) ?? ""
                  }
                  status={
                    (valueToLabel(
                      "festival_status",
                      award.award_status,
                      master
                    ) as string) ?? ""
                  }
                  onEdit={() => onEdit(index)}
                  onRemove={() => onRemove(index)}
                />
              ))}
            </RowList>
          )}

          <AddButton
            type="dashed"
            block
            icon={<MdAdd />}
            onClick={() => {
              awardForm.resetFields();
              setIsShowingAwardModal(true);
            }}
          >
            Add award
          </AddButton>
        </ListShell>
      </Section>
    </Wrapper>
  );
};

const AwardCard = ({
  award,
  winner,
  category,
  status,
  onEdit,
  onRemove,
}: {
  award: AwardModel;
  winner: string;
  category: string;
  status: string;
  onEdit: () => void;
  onRemove: () => void;
}) => (
  <RowShell>
    <YearBadge>
      <YearLabel>YEAR</YearLabel>
      <YearValue>{dayjs(award.award_year as any).format("YYYY")}</YearValue>
    </YearBadge>
    <RowBody>
      <RowHead>
        <FestivalName title={award.name_of_festival}>
          {award.name_of_festival}
        </FestivalName>
        {award.country ? <Country>{award.country}</Country> : null}
      </RowHead>
      <Meta>
        {category ? <MetaItem>{category}</MetaItem> : null}
        {status ? <StatusChip>{status}</StatusChip> : null}
      </Meta>
      {winner ? (
        <WinnerLine>
          <WinnerLabel>Winner</WinnerLabel>
          <WinnerName>{winner}</WinnerName>
        </WinnerLine>
      ) : null}
    </RowBody>
    <Actions>
      <IconBtn type="button" onClick={onEdit} aria-label="Edit">
        <MdEdit />
      </IconBtn>
      <IconBtn $danger type="button" onClick={onRemove} aria-label="Remove">
        <MdClose />
      </IconBtn>
    </Actions>
  </RowShell>
);

const Wrapper = styled.div`
  width: 100%;
  flex-direction: column;
`;

const ListShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-width: 0;
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const RowShell = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.4rem;
  padding: 1.4rem;
  background-color: ${card.innerBg};
  border: 1px solid ${card.border};
  border-radius: 0.9rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${colors.primary.hex};
    box-shadow: 0 1px 6px rgba(32, 84, 150, 0.06);
  }
`;

const YearBadge = styled.div`
  flex: 0 0 auto;
  width: 6.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 0.7rem;
  padding: 0.6rem 0.4rem;
  gap: 0.2rem;
`;

const YearLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const YearValue = styled.span`
  font-size: 1.85rem;
  font-weight: 700;
  color: ${colors.primary.hex};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
`;

const RowHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.4rem 0.8rem;
`;

const FestivalName = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.text.hex};
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
`;

const Country = styled.span`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.55;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.8rem;
`;

const MetaItem = styled.span`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.7;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.7rem;
  font-size: 1.1rem;
  font-weight: 500;
  color: ${colors.primary.hex};
  background: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 999px;
  letter-spacing: 0.01em;
`;

const WinnerLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.85;
`;

const WinnerLabel = styled.span`
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.04em;
  opacity: 0.65;
`;

const WinnerName = styled.span`
  font-weight: 500;
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.2rem;
`;

const IconBtn = styled.button<{ $danger?: boolean }>`
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${colors.text.hex};
  opacity: 0.6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.7rem;
  transition: background 0.15s, color 0.15s, opacity 0.15s;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? "rgba(239, 35, 60, 0.1)" : card.accentSoft};
    color: ${({ $danger }) =>
      $danger ? colors.error.hex : colors.primary.hex};
    opacity: 1;
  }
`;

const AddButton = styled(Button)`
  height: 4rem;
  border-radius: 0.8rem;
  font-weight: 500;
  border-style: dashed;
  border-color: ${card.border};
  background: transparent;
  color: ${colors.primary.hex};

  &:hover {
    border-color: ${colors.primary.hex} !important;
    color: ${colors.primary.hex} !important;
    background: ${card.accentSoft} !important;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 3rem 1.6rem;
  background-color: ${card.innerBg};
  border: 1px dashed ${card.border};
  border-radius: 0.9rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background: ${card.accentSoft};
  color: ${colors.primary.hex};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2.6rem;
  margin-bottom: 0.6rem;
`;

const EmptyTitle = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${colors.text.hex};
`;

const EmptyHint = styled.div`
  font-size: 1.2rem;
  color: ${colors.text.hex};
  opacity: 0.6;
  line-height: 1.4;
  max-width: 38rem;
`;
